import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useMatch } from "react-router-dom";
import { HelpModal, loadHelpCenter } from "./HelpModal.tsx";
import { AppearanceModal } from "./AppearanceModal.tsx";
import { BoardDefs } from "./BoardDefs.tsx";
import { PracticeMenu } from "../trainer/PracticeMenu.tsx";
import { ScrollManager } from "./ScrollManager.tsx";
import { ResumeLink } from "./ResumeLink.tsx";
import { useTaglineFit } from "./useTaglineFit.ts";
import { loadCourseCopy } from "../../content/catalogCopy.ts";

export function AppShell({ children }: { children: ReactNode }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const helpBtnRef = useRef<HTMLButtonElement>(null);
  const appearanceBtnRef = useRef<HTMLButtonElement>(null);
  const headerRef = useTaglineFit<HTMLElement>();

  // Fetch the Help text (see HelpModal) and the course guides once the page is idle.
  useEffect(() => {
    const prefetch = () => {
      void loadHelpCenter().catch(() => {});
      void loadCourseCopy().catch(() => {});
    };
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    // Safari has no requestIdleCallback.
    const timer = setTimeout(prefetch, 2000);
    return () => clearTimeout(timer);
  }, []);
  const onTheoryPage = useMatch("/opening/:slug");
  // The filled style (nav-btn-browse) marks the page you are on.
  const navClass = (current: boolean) => `nav-btn${current ? " nav-btn-browse" : ""}`;

  const closeHelp = () => {
    setHelpOpen(false);
    helpBtnRef.current?.focus();
  };
  const closeAppearance = () => {
    setAppearanceOpen(false);
    appearanceBtnRef.current?.focus();
  };

  return (
    <>
      <ScrollManager />
      <header className="site-header" ref={headerRef}>
        <NavLink to="/" className="logo" aria-label="The First 15 Moves home">
          <svg className="logo-mark" viewBox="0 0 44 44" aria-hidden="true" focusable="false">
            <circle cx="22" cy="22" r="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <text
              x="22"
              y="28"
              textAnchor="middle"
              fontFamily="Fraunces, Georgia, serif"
              fontSize="16"
              fontWeight="600"
              fill="currentColor"
            >
              15
            </text>
          </svg>
        </NavLink>
        <h1 className="header-tagline">
          <span>Master the first 15</span> <span>moves of chess.</span>
        </h1>
        <div className="tagline-rulers" aria-hidden="true">
          <span className="header-tagline tagline-ruler-one">
            <span>Master the first 15</span> <span>moves of chess.</span>
          </span>
          <span className="header-tagline tagline-ruler-two">
            <span>Master the first 15</span> <span>moves of chess.</span>
          </span>
        </div>
        <nav className="site-nav" aria-label="Main navigation">
          {onTheoryPage ? (
            // A Theory page sits inside Openings: same highlight, and screen
            // readers hear it as the current section.
            <Link className={navClass(true)} to="/openings" aria-current="true">
              Openings
            </Link>
          ) : (
            <NavLink className={({ isActive }) => navClass(isActive)} to="/openings">
              Openings
            </NavLink>
          )}
          <PracticeMenu />
          <ResumeLink />
          <NavLink className={({ isActive }) => navClass(isActive)} to="/progress">
            Progress
          </NavLink>
          <button
            ref={appearanceBtnRef}
            className="nav-btn nav-btn-appearance"
            type="button"
            aria-label="Appearance"
            title="Appearance: pieces, board and site"
            aria-haspopup="dialog"
            aria-controls="appearanceModal"
            aria-expanded={appearanceOpen}
            onClick={() => setAppearanceOpen(true)}
          >
            Appearance
          </button>
          <button
            ref={helpBtnRef}
            className="nav-btn"
            type="button"
            aria-haspopup="dialog"
            aria-controls="helpModal"
            aria-expanded={helpOpen}
            onClick={() => setHelpOpen(true)}
          >
            Help
          </button>
        </nav>
      </header>
      <HelpModal open={helpOpen} onClose={closeHelp} />
      <AppearanceModal open={appearanceOpen} onClose={closeAppearance} />
      <BoardDefs />
      <main id="main-content" className="page" tabIndex={-1}>
        {children}
      </main>
      <footer className="site-footer">
        <p>
          Built with{" "}
          <a href="https://github.com/jhlywa/chess.js" target="_blank" rel="noopener">
            chess.js
          </a>
          ,{" "}
          <a href="https://github.com/shaack/cm-chessboard" target="_blank" rel="noopener">
            cm-chessboard
          </a>
          , and{" "}
          <a href="https://stockfishchess.org/" target="_blank" rel="noopener">
            Stockfish
          </a>
          . Stockfish is distributed under the{" "}
          <a href="/vendor/stockfish/Copying.txt" target="_blank" rel="noopener">
            GPLv3
          </a>
          .
        </p>
        <p className="build-id">Build {__APP_VERSION__}</p>
      </footer>
    </>
  );
}
